import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { X, Check } from 'lucide-react';
import './ImageCropperModal.css';

const VIEWPORT_MAX_WIDTH = 600;
const VIEWPORT_MAX_HEIGHT = 400;

const toAspectRatio = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 3;
};

const computeCropBoxForDisplay = (displayWidth, displayHeight, naturalWidth, naturalHeight, aspectRatio) => {
  const ratio = toAspectRatio(aspectRatio);
  const imgRatio = naturalWidth / naturalHeight;

  if (ratio === 1) {
    const side = Math.min(displayWidth, displayHeight);
    return {
      x: (displayWidth - side) / 2,
      y: (displayHeight - side) / 2,
      width: side,
      height: side,
    };
  }

  if (imgRatio < ratio) {
    const cropWidth = displayWidth;
    const cropHeight = displayWidth / ratio;
    return {
      x: 0,
      y: (displayHeight - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight,
    };
  }

  const cropHeight = displayHeight;
  const cropWidth = displayHeight * ratio;
  return {
    x: (displayWidth - cropWidth) / 2,
    y: 0,
    width: cropWidth,
    height: cropHeight,
  };
};

const fitDisplaySize = (naturalWidth, naturalHeight) => {
  const maxW = Math.min(VIEWPORT_MAX_WIDTH, window.innerWidth - 64);
  const maxH = Math.min(VIEWPORT_MAX_HEIGHT, window.innerHeight - 250);
  // Never upscale — small images stay native size so the crop view does not look zoomed.
  const displayScale = Math.min(1, maxW / naturalWidth, maxH / naturalHeight);
  return {
    width: Math.max(1, Math.round(naturalWidth * displayScale)),
    height: Math.max(1, Math.round(naturalHeight * displayScale)),
  };
};

const ImageCropperModal = ({ imageSrc, onCropComplete, onClose, aspectRatio = 3, title = 'Adjust Banner Image' }) => {
  const lockedAspect = toAspectRatio(aspectRatio);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [cropBox, setCropBox] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [initialCropBox, setInitialCropBox] = useState({ x: 0, y: 0, width: 0, height: 0 });

  const imgRef = useRef(null);
  const loadTokenRef = useRef(0);
  const cropBoxRef = useRef(cropBox);
  const displaySizeRef = useRef(displaySize);
  cropBoxRef.current = cropBox;
  displaySizeRef.current = displaySize;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const applyImageLayout = useCallback((img) => {
    const naturalWidth = img?.naturalWidth || 0;
    const naturalHeight = img?.naturalHeight || 0;
    if (!naturalWidth || !naturalHeight) return false;

    const { width: displayWidth, height: displayHeight } = fitDisplaySize(naturalWidth, naturalHeight);
    setNaturalSize({ width: naturalWidth, height: naturalHeight });
    setDisplaySize({ width: displayWidth, height: displayHeight });
    setCropBox(
      computeCropBoxForDisplay(displayWidth, displayHeight, naturalWidth, naturalHeight, lockedAspect),
    );
    setImageLoaded(true);
    return true;
  }, [lockedAspect]);

  // Reset on each new image. If the browser already decoded it (common for data URLs /
  // the 2nd+ product image), onLoad may have fired before this effect — re-apply layout.
  useLayoutEffect(() => {
    const token = loadTokenRef.current + 1;
    loadTokenRef.current = token;
    setImageLoaded(false);
    setNaturalSize({ width: 0, height: 0 });
    setDisplaySize({ width: 0, height: 0 });
    setCropBox({ x: 0, y: 0, width: 0, height: 0 });
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);

    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0 && loadTokenRef.current === token) {
      applyImageLayout(img);
    }
  }, [imageSrc, lockedAspect, applyImageLayout]);

  const handleImageLoad = (e) => {
    applyImageLayout(e.target);
  };

  useEffect(() => {
    const handleResize = () => {
      if (!imageLoaded || !naturalSize.width || !naturalSize.height) return;

      const { width: displayWidth, height: displayHeight } = fitDisplaySize(
        naturalSize.width,
        naturalSize.height,
      );
      const nextBox = computeCropBoxForDisplay(
        displayWidth,
        displayHeight,
        naturalSize.width,
        naturalSize.height,
        lockedAspect,
      );

      const prevBox = cropBoxRef.current;
      const prevDisplay = displaySizeRef.current;
      if (lockedAspect === 1 && prevBox.width > 0 && prevDisplay.width > 0) {
        const prevRelX = prevBox.x / (prevDisplay.width - prevBox.width || 1);
        const prevRelY = prevBox.y / (prevDisplay.height - prevBox.height || 1);
        nextBox.x = Number.isFinite(prevRelX)
          ? prevRelX * (displayWidth - nextBox.width)
          : nextBox.x;
        nextBox.y = Number.isFinite(prevRelY)
          ? prevRelY * (displayHeight - nextBox.height)
          : nextBox.y;
      }

      setDisplaySize({ width: displayWidth, height: displayHeight });
      setCropBox(nextBox);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [imageLoaded, naturalSize.width, naturalSize.height, lockedAspect]);

  const handleMouseDown = (e) => {
    if (!imageLoaded) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - cropBox.x, y: e.clientY - cropBox.y });
  };

  const handleTouchStart = (e) => {
    if (!imageLoaded || e.touches.length !== 1) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.touches[0].clientX - cropBox.x,
      y: e.touches[0].clientY - cropBox.y,
    });
  };

  const handleHandleMouseDown = (e, handle) => {
    if (!imageLoaded) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeHandle(handle);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialCropBox({ ...cropBox });
  };

  const handleHandleTouchStart = (e, handle) => {
    if (!imageLoaded || e.touches.length !== 1) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeHandle(handle);
    setDragStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    });
    setInitialCropBox({ ...cropBox });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!imageLoaded) return;
      if (!isDragging && !isResizing) return;
      
      let clientX, clientY;
      if (e.touches) {
        if (e.touches.length !== 1) return;
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      if (isResizing && resizeHandle) {
        const dx = clientX - dragStart.x;
        const dy = clientY - dragStart.y;

        let targetWidth = initialCropBox.width;
        let targetHeight = initialCropBox.height;

        if (resizeHandle === 'br') {
          targetWidth = initialCropBox.width + dx;
          targetHeight = targetWidth / lockedAspect;

          const maxAllowedWidth = displaySize.width - initialCropBox.x;
          const maxAllowedHeight = displaySize.height - initialCropBox.y;

          if (targetWidth > maxAllowedWidth) {
            targetWidth = maxAllowedWidth;
            targetHeight = targetWidth / lockedAspect;
          }
          if (targetHeight > maxAllowedHeight) {
            targetHeight = maxAllowedHeight;
            targetWidth = targetHeight * lockedAspect;
          }

          if (targetWidth < 90) {
            targetWidth = 90;
            targetHeight = 90 / lockedAspect;
          }

          setCropBox({
            x: initialCropBox.x,
            y: initialCropBox.y,
            width: targetWidth,
            height: targetHeight
          });
        }
        else if (resizeHandle === 'tr') {
          targetWidth = initialCropBox.width + dx;
          targetHeight = targetWidth / lockedAspect;

          const maxAllowedWidth = displaySize.width - initialCropBox.x;
          const maxAllowedHeight = initialCropBox.y + initialCropBox.height;

          if (targetWidth > maxAllowedWidth) {
            targetWidth = maxAllowedWidth;
            targetHeight = targetWidth / lockedAspect;
          }
          if (targetHeight > maxAllowedHeight) {
            targetHeight = maxAllowedHeight;
            targetWidth = targetHeight * lockedAspect;
          }

          if (targetWidth < 90) {
            targetWidth = 90;
            targetHeight = 90 / lockedAspect;
          }

          setCropBox({
            x: initialCropBox.x,
            y: initialCropBox.y + (initialCropBox.height - targetHeight),
            width: targetWidth,
            height: targetHeight
          });
        }
        else if (resizeHandle === 'bl') {
          targetWidth = initialCropBox.width - dx;
          targetHeight = targetWidth / lockedAspect;

          const maxAllowedWidth = initialCropBox.x + initialCropBox.width;
          const maxAllowedHeight = displaySize.height - initialCropBox.y;

          if (targetWidth > maxAllowedWidth) {
            targetWidth = maxAllowedWidth;
            targetHeight = targetWidth / lockedAspect;
          }
          if (targetHeight > maxAllowedHeight) {
            targetHeight = maxAllowedHeight;
            targetWidth = targetHeight * lockedAspect;
          }

          if (targetWidth < 90) {
            targetWidth = 90;
            targetHeight = 90 / lockedAspect;
          }

          setCropBox({
            x: initialCropBox.x + (initialCropBox.width - targetWidth),
            y: initialCropBox.y,
            width: targetWidth,
            height: targetHeight
          });
        }
        else if (resizeHandle === 'tl') {
          targetWidth = initialCropBox.width - dx;
          targetHeight = targetWidth / lockedAspect;

          const maxAllowedWidth = initialCropBox.x + initialCropBox.width;
          const maxAllowedHeight = initialCropBox.y + initialCropBox.height;

          if (targetWidth > maxAllowedWidth) {
            targetWidth = maxAllowedWidth;
            targetHeight = targetWidth / lockedAspect;
          }
          if (targetHeight > maxAllowedHeight) {
            targetHeight = maxAllowedHeight;
            targetWidth = targetHeight * lockedAspect;
          }

          if (targetWidth < 90) {
            targetWidth = 90;
            targetHeight = 90 / lockedAspect;
          }

          setCropBox({
            x: initialCropBox.x + (initialCropBox.width - targetWidth),
            y: initialCropBox.y + (initialCropBox.height - targetHeight),
            width: targetWidth,
            height: targetHeight
          });
        }
      }
      else if (isDragging) {
        let newX = clientX - dragStart.x;
        let newY = clientY - dragStart.y;

        newX = Math.min(displaySize.width - cropBox.width, Math.max(0, newX));
        newY = Math.min(displaySize.height - cropBox.height, Math.max(0, newY));

        setCropBox((prev) => ({ ...prev, x: newX, y: newY }));
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle(null);
    };

    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleMouseMove, { passive: false });
      window.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleMouseMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, imageLoaded, initialCropBox, displaySize, cropBox.width, cropBox.height, resizeHandle, lockedAspect]);

  const handleCrop = () => {
    const img = imgRef.current;
    if (!img || !imageLoaded) return;

    const canvas = document.createElement('canvas');
    let canvasWidth = 1200;
    let canvasHeight = 1200 / lockedAspect;
    if (lockedAspect === 1) {
      canvasWidth = 1200;
      canvasHeight = 1200;
    } else if (lockedAspect < 1) {
      canvasHeight = 1000;
      canvasWidth = 1000 * lockedAspect;
    }
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');

    const scale = naturalSize.width / displaySize.width;
    const sourceX = cropBox.x * scale;
    const sourceY = cropBox.y * scale;
    const sourceWidth = cropBox.width * scale;
    const sourceHeight = cropBox.height * scale;

    ctx.drawImage(
      img,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      canvasWidth,
      canvasHeight
    );

    canvas.toBlob((blob) => {
      if (blob) {
        const croppedFile = new File([blob], 'cropped-banner.jpg', { type: 'image/jpeg' });
        onCropComplete(croppedFile, canvas.toDataURL('image/jpeg'));
      }
    }, 'image/jpeg', 0.95);
  };

  const aspectHint = lockedAspect === 1
    ? 'Drag to reposition · crop area is 1:1 (square)'
    : `Drag to reposition · locked to ${lockedAspect > 1 ? `${lockedAspect}:1` : `1:${(1 / lockedAspect).toFixed(2).replace(/\.?0+$/, '')}`} aspect`;

  return (
    <div className="crop-modal-overlay">
      <div className="crop-modal">
        <div className="crop-modal-header">
          <h3>{title}</h3>
          <button type="button" className="crop-modal-close-btn" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="crop-modal-body">
          <p className="crop-instructions">{aspectHint}</p>
          <div 
            className="crop-area-container"
            style={{ 
              width: displaySize.width || 'auto', 
              height: displaySize.height || 'auto' 
            }}
          >
            {imageSrc && (
              <img
                key={imageSrc}
                ref={imgRef}
                src={imageSrc}
                alt="Original source"
                onLoad={handleImageLoad}
                crossOrigin="anonymous"
                className="crop-source-image"
                style={{
                  width: displaySize.width ? `${displaySize.width}px` : undefined,
                  height: displaySize.height ? `${displaySize.height}px` : undefined,
                  visibility: imageLoaded ? 'visible' : 'hidden',
                }}
                draggable={false}
              />
            )}

            {imageLoaded && (
              <>
                {/* Single unified dimming mask with a cutout polygon using clip-path */}
                <div 
                  className="crop-mask-overlay" 
                  style={{ 
                    top: 0, 
                    left: 0, 
                    width: '100%', 
                    height: '100%',
                    clipPath: `polygon(
                      0px 0px, 
                      0px ${displaySize.height}px, 
                      ${displaySize.width}px ${displaySize.height}px, 
                      ${displaySize.width}px 0px, 
                      0px 0px, 
                      ${cropBox.x}px ${cropBox.y}px, 
                      ${cropBox.x + cropBox.width}px ${cropBox.y}px, 
                      ${cropBox.x + cropBox.width}px ${cropBox.y + cropBox.height}px, 
                      ${cropBox.x}px ${cropBox.y + cropBox.height}px, 
                      ${cropBox.x}px ${cropBox.y}px
                    )`,
                    WebkitClipPath: `polygon(
                      0px 0px, 
                      0px ${displaySize.height}px, 
                      ${displaySize.width}px ${displaySize.height}px, 
                      ${displaySize.width}px 0px, 
                      0px 0px, 
                      ${cropBox.x}px ${cropBox.y}px, 
                      ${cropBox.x + cropBox.width}px ${cropBox.y}px, 
                      ${cropBox.x + cropBox.width}px ${cropBox.y + cropBox.height}px, 
                      ${cropBox.x}px ${cropBox.y + cropBox.height}px, 
                      ${cropBox.x}px ${cropBox.y}px
                    )`
                  }} 
                />

                {/* Movable transparent Crop Box */}
                <div
                  className="crop-interactive-box"
                  style={{
                    left: `${cropBox.x}px`,
                    top: `${cropBox.y}px`,
                    width: `${cropBox.width}px`,
                    height: `${cropBox.height}px`,
                    cursor: isDragging ? 'grabbing' : 'grab',
                  }}
                  onMouseDown={handleMouseDown}
                  onTouchStart={handleTouchStart}
                >
                  <div className="crop-box-border" />
                  <div className="crop-box-grid" />
                  <div
                    className="crop-box-handle handle-tl"
                    onMouseDown={(e) => handleHandleMouseDown(e, 'tl')}
                    onTouchStart={(e) => handleHandleTouchStart(e, 'tl')}
                  />
                  <div
                    className="crop-box-handle handle-tr"
                    onMouseDown={(e) => handleHandleMouseDown(e, 'tr')}
                    onTouchStart={(e) => handleHandleTouchStart(e, 'tr')}
                  />
                  <div
                    className="crop-box-handle handle-bl"
                    onMouseDown={(e) => handleHandleMouseDown(e, 'bl')}
                    onTouchStart={(e) => handleHandleTouchStart(e, 'bl')}
                  />
                  <div
                    className="crop-box-handle handle-br"
                    onMouseDown={(e) => handleHandleMouseDown(e, 'br')}
                    onTouchStart={(e) => handleHandleTouchStart(e, 'br')}
                  />
                  <div className="crop-box-handle handle-t" style={{ display: 'none' }} />
                  <div className="crop-box-handle handle-b" style={{ display: 'none' }} />
                  <div className="crop-box-handle handle-l" style={{ display: 'none' }} />
                  <div className="crop-box-handle handle-r" style={{ display: 'none' }} />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="crop-modal-footer">
          <button type="button" className="crop-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="crop-btn-apply" 
            onClick={handleCrop}
            disabled={!imageLoaded}
          >
            <Check size={16} style={{ marginRight: '6px' }} />
            Crop & Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropperModal;
